/**
 * Game Instructions Data — Step-by-step how-to-play and rules for all 26 games
 *
 * Used by the HowToPlay foldable component at the bottom of each game.
 * Each game exports a GameInstructions object with steps, rules, and tips.
 */

import { GameInstructions } from '../components/HowToPlay';

export const tictactoeInstructions: GameInstructions = {
  gameName: 'Tic-Tac-Toe',
  steps: [
    'Choose your symbol (X or O) and who goes first.',
    'On your turn, click any empty cell on the 3×3 grid.',
    'Your symbol is placed in that cell.',
    'Alternate turns with your opponent.',
    'Get three of your symbols in a row (horizontal, vertical, or diagonal) to win.',
    'If all 9 cells are filled with no winner, the game is a draw.',
  ],
  rules: [
    'The board is a 3×3 grid with 9 cells.',
    'Two players take turns — one is X, the other is O.',
    'X always goes first.',
    'A cell can only be claimed once.',
    'First player to align 3 symbols in a row wins.',
    'If the board fills up with no three-in-a-row, it is a draw.',
  ],
  tips: [
    'Take the center square if available — it gives the most winning paths.',
    'If your opponent takes the center, take a corner.',
    'Always block your opponent if they have two in a row.',
    'Try to create a "fork" — two ways to win at once.',
  ],
};

export const chessInstructions: GameInstructions = {
  gameName: 'Chess',
  steps: [
    'White moves first. Click a piece to select it.',
    'Valid move squares are highlighted — click one to move.',
    'Capture opponent pieces by moving to their square.',
    'Protect your King from check (attack). You must escape check immediately.',
    'Use special moves: castling (King + Rook), en passant, and pawn promotion.',
    'Checkmate the opponent\'s King (no escape from check) to win.',
  ],
  rules: [
    'Each player has 16 pieces: 1 King, 1 Queen, 2 Rooks, 2 Bishops, 2 Knights, 8 Pawns.',
    'Pieces move differently: King (1 square any direction), Queen (any direction any distance), Rook (straight lines), Bishop (diagonals), Knight (L-shape, can jump), Pawn (forward 1, captures diagonally).',
    'Pawns can move 2 squares on their first move.',
    'A pawn reaching the opposite end promotes to Queen, Rook, Bishop, or Knight.',
    'Castling: King moves 2 squares toward a Rook, Rook jumps over (only if neither has moved and path is clear).',
    'En passant: A pawn can capture an adjacent pawn that just moved 2 squares.',
    'Check: Your King is under attack. You must block, capture the attacker, or move the King.',
    'Checkmate: King is in check with no legal escape — game over.',
    'Stalemate: Player has no legal moves but is not in check — draw.',
  ],
  tips: [
    'Control the center of the board early.',
    'Develop your Knights and Bishops before moving the Queen.',
    'Castle early to protect your King.',
    'Don\'t move the same piece twice in the opening without reason.',
    'Look for forks, pins, and skewers to win material.',
  ],
};

export const checkersInstructions: GameInstructions = {
  gameName: 'Checkers',
  steps: [
    'Dark pieces move first. Click a piece to select it.',
    'Move diagonally forward to an adjacent empty dark square.',
    'Jump over an opponent\'s piece to capture it (mandatory if available).',
    'Multiple jumps in one turn are allowed if available.',
    'Reach the opposite end to crown your piece as a King.',
    'Capture all opponent pieces or block all their moves to win.',
  ],
  rules: [
    'Played on an 8×8 board using only the dark squares.',
    'Each player starts with 12 pieces on the dark squares of their first 3 rows.',
    'Regular pieces move diagonally forward only.',
    'Kings (crowned pieces) can move diagonally forward and backward.',
    'Captures are mandatory — you must jump if you can.',
    'Multi-jump: If after a capture another jump is available, you must continue.',
    'A piece reaching the last row becomes a King.',
    'Win by capturing all opponent pieces or leaving them with no legal moves.',
  ],
  tips: [
    'Control the center of the board.',
    'Keep pieces on the back row as long as possible for defense.',
    'Try to get Kings early — they are much more powerful.',
    'Force exchanges when you have more pieces.',
    'Watch for double-jump opportunities.',
  ],
};

export const connectFourInstructions: GameInstructions = {
  gameName: 'Connect Four',
  steps: [
    'Choose your color (Red or Yellow). Red goes first.',
    'On your turn, click a column to drop your disc.',
    'The disc falls to the lowest available row in that column.',
    'Alternate turns with your opponent.',
    'Connect 4 of your discs in a row (horizontal, vertical, or diagonal) to win.',
    'If the board fills up (42 discs) with no winner, it\'s a draw.',
  ],
  rules: [
    'The board is 7 columns × 6 rows (42 slots total).',
    'Discs drop to the bottom of the chosen column (gravity).',
    'A column is full when all 6 rows are occupied.',
    'First to connect 4 in any direction wins.',
    'Red always plays first.',
  ],
  tips: [
    'Control the center column — it\'s part of the most winning combinations.',
    'Build threats in multiple directions simultaneously.',
    'Watch for diagonal traps — they\'re easy to miss.',
    'Try to force your opponent to play where you want.',
    'Odd/even strategy: count rows to plan ahead.',
  ],
};

export const battleshipInstructions: GameInstructions = {
  gameName: 'Battleship',
  steps: [
    'Place your ships on your grid (drag to position, click to rotate).',
    'Ships cannot overlap or go off the grid.',
    'Once both players have placed ships, the battle begins.',
    'On your turn, click a cell on the opponent\'s grid to fire.',
    'Hits are marked red, misses are marked white.',
    'Sink all 5 of your opponent\'s ships to win.',
  ],
  rules: [
    'Each player has a 10×10 grid.',
    'Fleet: Carrier (5), Battleship (4), Cruiser (3), Submarine (3), Destroyer (2).',
    'Ships are placed horizontally or vertically (not diagonally).',
    'Ships cannot overlap or extend beyond the grid.',
    'Players alternate firing one shot per turn.',
    'A ship is sunk when all its cells are hit.',
    'First player to sink all opponent ships wins.',
  ],
  tips: [
    'Don\'t cluster your ships together.',
    'Use a checkerboard pattern when hunting — it covers more ground.',
    'Once you get a hit, fire adjacent cells to find the ship\'s direction.',
    'Place ships away from edges for less predictability.',
    'Remember: the smallest ship is 2 cells, so skip single-cell gaps.',
  ],
};

export const pokerInstructions: GameInstructions = {
  gameName: 'Poker (Texas Hold\'em)',
  steps: [
    'Each player is dealt 2 private cards (hole cards).',
    'Betting round 1 (Pre-flop): Bet, call, raise, or fold based on your hole cards.',
    '3 community cards are dealt face-up (the Flop).',
    'Betting round 2 (Flop): Bet based on your hand + community cards.',
    '4th community card dealt (the Turn), then betting round 3.',
    '5th community card dealt (the River), then final betting round.',
    'Remaining players reveal cards — best 5-card hand wins the pot.',
  ],
  rules: [
    'Hand rankings (high to low): Royal Flush, Straight Flush, Four of a Kind, Full House, Flush, Straight, Three of a Kind, Two Pair, One Pair, High Card.',
    'Make the best 5-card hand from your 2 hole cards + 5 community cards.',
    'Blinds: Small blind and big blind are forced bets to start action.',
    'Actions: Fold (quit hand), Check (pass), Call (match bet), Raise (increase bet), All-in.',
    'If all but one player folds, the remaining player wins without showing cards.',
    'Side pots are created when a player goes all-in with less than others bet.',
  ],
  tips: [
    'Position matters — acting last gives you more information.',
    'Don\'t play too many hands. Be selective with starting cards.',
    'Pay attention to the community cards and possible opponent hands.',
    'Bluffing works best against fewer opponents.',
    'Manage your chip stack — don\'t risk everything on marginal hands.',
  ],
};

export const mafiaInstructions: GameInstructions = {
  gameName: 'Mafia',
  steps: [
    'Players are secretly assigned roles: Mafia, Town, or Special (Doctor, Detective).',
    'Day phase: All players discuss and vote to eliminate a suspect.',
    'The player with the most votes is eliminated and their role is revealed.',
    'Night phase: Mafia secretly chooses a player to eliminate.',
    'Special roles act at night: Doctor protects, Detective investigates.',
    'Repeat day/night cycles until Mafia is eliminated (Town wins) or Mafia equals Town (Mafia wins).',
  ],
  rules: [
    'Minimum 6 players recommended (2 Mafia, 4 Town).',
    'Mafia members know each other; Town does not know who is Mafia.',
    'During the day, anyone can be voted out by majority.',
    'During the night, Mafia unanimously picks a target to kill.',
    'Doctor can save one player per night (including themselves).',
    'Detective can investigate one player per night to learn their role.',
    'Town wins when all Mafia are eliminated.',
    'Mafia wins when they equal or outnumber Town.',
    'Eliminated players cannot communicate with living players.',
  ],
  tips: [
    'As Town: Pay attention to voting patterns and who defends whom.',
    'As Mafia: Act natural, don\'t over-defend your teammates.',
    'As Detective: Don\'t reveal your role too early.',
    'As Doctor: Protect the confirmed Town members.',
    'Watch for inconsistencies in people\'s stories.',
  ],
};

export const ludoInstructions: GameInstructions = {
  gameName: 'Ludo',
  steps: [
    'Each player has 4 tokens in their home base.',
    'Roll the dice — you need a 6 to move a token out of home base.',
    'Move your token clockwise around the board by the dice value.',
    'Rolling a 6 gives you an extra turn.',
    'Land on an opponent\'s token to send it back to their home base.',
    'Navigate your token into the home column and reach the center to finish.',
    'First player to get all 4 tokens to the center wins.',
  ],
  rules: [
    'Board has a cross-shaped track with 52 common squares.',
    'Each player has a colored home base and home column.',
    'A 6 is required to enter a token onto the track.',
    'Rolling a 6 grants an extra roll (max 3 consecutive 6s = turn lost).',
    'Landing on an opponent sends them back to their home base.',
    'Safe squares (marked with stars) protect tokens from capture.',
    'Tokens enter the home column after completing the circuit.',
    'Exact roll needed to reach the center (home).',
    'Two same-color tokens on one square form a block (cannot be passed).',
  ],
  tips: [
    'Spread your tokens — don\'t rely on just one.',
    'Use 6s strategically: enter new tokens or advance existing ones.',
    'Try to land on safe squares when opponents are nearby.',
    'Block opponents by keeping two tokens together.',
    'Prioritize getting tokens into the home column where they\'re safe.',
  ],
};

export const snakesAndLaddersInstructions: GameInstructions = {
  gameName: 'Snakes & Ladders',
  steps: [
    'Roll the dice to determine how many squares to move.',
    'Move your token forward by the dice value.',
    'Land on a ladder base? Climb up to the top!',
    'Land on a snake head? Slide down to the tail!',
    'First player to reach or pass square 100 wins.',
  ],
  rules: [
    'Board has 100 numbered squares arranged in a 10×10 grid.',
    'Players take turns rolling a single die.',
    'Move forward by the exact dice value.',
    'Ladders: Landing on the bottom takes you to the top.',
    'Snakes: Landing on the head sends you to the tail.',
    'Some variants require exact roll to reach 100.',
    'No captures — multiple tokens can share a square.',
  ],
  tips: [
    'This is purely a game of luck — no strategy involved!',
    'Enjoy the ups and downs.',
    'Great game for teaching kids about numbers and counting.',
  ],
};

export const teenPattiInstructions: GameInstructions = {
  gameName: 'Teen Patti',
  steps: [
    'Each player places an ante (boot) into the pot.',
    'Dealer gives 3 cards face-down to each player.',
    'Choose to play "Blind" (without seeing cards) or "Seen" (after looking).',
    'Betting rounds: Call (match), Raise (increase), or Fold (quit).',
    'Blind players bet half the amount of Seen players.',
    'Continue until only 2 players remain, then request a "Show".',
    'Best 3-card hand wins the pot.',
  ],
  rules: [
    'Hand rankings (high to low): Trail/Set (three of a kind), Pure Sequence (straight flush), Sequence (straight), Color (flush), Pair, High Card.',
    'Blind player bets 1x or 2x the current stake; Seen player bets 2x or 4x.',
    'A Blind player can ask for a Show only when 2 players remain.',
    'A Seen player pays for the Show (2x current stake).',
    'In a Show, higher hand wins. If equal, the player who paid for Show loses.',
    'Sideshow: A Seen player can privately compare with the previous Seen player.',
  ],
  tips: [
    'Playing Blind early saves money and builds suspense.',
    'Watch opponents\' betting patterns for tells.',
    'Don\'t chase bad hands — fold early to save chips.',
    'Bluffing is powerful in Teen Patti since hands are only 3 cards.',
  ],
};

export const reversiInstructions: GameInstructions = {
  gameName: 'Reversi (Othello)',
  steps: [
    'Black places first. Click a valid cell to place your disc.',
    'You must place adjacent to an opponent\'s disc and "sandwich" their discs.',
    'All sandwiched opponent discs flip to your color.',
    'If you cannot make a valid move, your turn is skipped.',
    'The game ends when the board is full or neither player can move.',
    'Player with the most discs of their color wins.',
  ],
  rules: [
    'Played on an 8×8 board. Starts with 2 black and 2 white discs in the center.',
    'A valid move must outflank (sandwich) at least one opponent disc.',
    'Outflanking works in all 8 directions (horizontal, vertical, diagonal).',
    'All outflanked discs in all directions are flipped in one move.',
    'If a player has no valid moves, they pass.',
    'Game ends when board is full or both players pass consecutively.',
    'Most discs wins. Ties are possible.',
  ],
  tips: [
    'Corners are the most valuable — they can never be flipped.',
    'Avoid placing next to corners (gives opponent the corner).',
    'Edges are strong but not as safe as corners.',
    'Having fewer discs early can be an advantage (more mobility).',
    'Control the center and limit your opponent\'s moves.',
  ],
};

export const mancalaInstructions: GameInstructions = {
  gameName: 'Mancala',
  steps: [
    'The board has 2 rows of 6 pits, each starting with 4 stones.',
    'On your turn, pick up all stones from one of your pits.',
    'Drop stones one-by-one counter-clockwise into subsequent pits.',
    'Skip your opponent\'s store (large pit) when distributing.',
    'If your last stone lands in your store, take another turn.',
    'If your last stone lands in an empty pit on your side, capture that stone plus the opposite pit\'s stones.',
    'Game ends when one side is empty. Most stones in your store wins.',
  ],
  rules: [
    'Each player owns the 6 pits on their side and the store to their right.',
    'You can only pick up stones from your own pits.',
    'Stones are distributed counter-clockwise, one per pit.',
    'Skip opponent\'s store, but drop in your own store.',
    'Landing last stone in your store = free turn.',
    'Landing last stone in empty own pit = capture (your stone + opposite stones go to your store).',
    'Game ends when all 6 pits on one side are empty.',
    'Remaining stones on the other side go to that player\'s store.',
    'Player with more stones (out of 48 total) wins.',
  ],
  tips: [
    'Aim for free turns by landing your last stone in your store.',
    'Set up captures by emptying pits across from opponent\'s full pits.',
    'Count stones to predict where your last stone will land.',
    'Keep stones on your side to maintain options.',
    'Watch for opponent\'s capture setups and block them.',
  ],
};

export const pachisiInstructions: GameInstructions = {
  gameName: 'Pachisi',
  steps: [
    'All pawns start in the Charkoni (center of the board).',
    'Roll cowrie shells (or dice) — need a grace throw to enter the track.',
    'Move your pawn counter-clockwise around the cross-shaped track.',
    'Land on an opponent to send them back to the Charkoni.',
    'Castle squares (marked ✕) are safe — no captures allowed there.',
    'Navigate into your home column and reach the Charkoni to finish.',
    'First player (or team) to get all pawns home wins.',
  ],
  rules: [
    'Cross-shaped board with 4 arms, each having 3 columns of 8 squares.',
    'Pawns start in the center (Charkoni) and need a grace throw to enter.',
    'Grace throws: 6, 10, or 25 (depending on variant).',
    '12 castle squares (3 per arm) are safe from capture.',
    'Landing on an opponent\'s pawn sends it back to start.',
    'Two same-color pawns on one square form a block.',
    'Exact roll needed to enter the Charkoni (home).',
    'Teams: Red+Green vs Blue+Yellow (in 4-player version).',
  ],
  tips: [
    'Use castle squares as resting points when opponents are near.',
    'Form blocks with your partner\'s pawns for defense.',
    'Prioritize capturing opponents near their home column.',
    'Balance between advancing and protecting your pawns.',
  ],
};

export const carromInstructions: GameInstructions = {
  gameName: 'Carrom',
  steps: [
    'Place the striker on your baseline.',
    'Flick the striker to hit carrom men into corner pockets.',
    'Pocket your assigned color (white or black) pieces.',
    'You must "cover" the Queen (red piece) by pocketing another piece immediately after.',
    'If you pocket a piece, you get another turn.',
    'First player to pocket all their pieces (+ Queen if claimed) wins.',
  ],
  rules: [
    'Board has 9 white, 9 black carrom men, and 1 red Queen.',
    'Players/teams are assigned white or black pieces.',
    'Striker must be flicked from behind your baseline.',
    'Pocketing your piece = extra turn. Pocketing opponent\'s = turn ends.',
    'Queen must be "covered" (pocket another piece same turn or next).',
    'If striker goes in (foul): return one of your pocketed pieces to center.',
    'Pocketing opponent\'s last piece before yours = you lose.',
    'First to clear all their pieces wins the board.',
  ],
  tips: [
    'Master the thumb flick for power and the finger flick for precision.',
    'Aim for clusters to break them apart early.',
    'Claim the Queen when you have a good cover shot lined up.',
    'Use bank shots (rebounds off the frame) for tricky angles.',
    'Control the center of the board.',
  ],
};

export const backgammonInstructions: GameInstructions = {
  gameName: 'Backgammon',
  steps: [
    'Each player rolls one die — higher roll goes first.',
    'Roll two dice on your turn.',
    'Move your checkers forward by the dice values (can split between 2 checkers).',
    'Move all 15 checkers around the board to your home board.',
    'Once all your checkers are in your home board, start "bearing off" (removing them).',
    'First player to bear off all 15 checkers wins.',
  ],
  rules: [
    'Board has 24 triangular points, divided into 4 quadrants of 6.',
    'Each player has 15 checkers starting in a specific arrangement.',
    'Checkers move in opposite directions (one player clockwise, other counter-clockwise).',
    'You must use both dice values if possible (all 4 if doubles).',
    'A single checker on a point is a "blot" — it can be hit and sent to the bar.',
    'A checker on the bar must re-enter before any other moves.',
    'Two or more checkers on a point make it safe (opponent cannot land).',
    'Bearing off: Remove checkers from your home board using dice rolls.',
    'Doubling cube: Players can double the stakes during the game.',
  ],
  tips: [
    'Make points (2+ checkers) to block your opponent.',
    'Don\'t leave blots in your opponent\'s home board.',
    'Build a "prime" (6 consecutive points) to trap opponent checkers.',
    'Balance between racing ahead and maintaining a strong position.',
    'Use the doubling cube when you have a clear advantage.',
  ],
};

export const unoInstructions: GameInstructions = {
  gameName: 'UNO',
  steps: [
    'Each player is dealt 7 cards. One card is placed face-up to start the discard pile.',
    'On your turn, play a card that matches the top card by color, number, or symbol.',
    'If you can\'t play, draw a card from the deck.',
    'Use action cards (Skip, Reverse, Draw Two) to disrupt opponents.',
    'When you have 1 card left, you must call "UNO!"',
    'First player to play all their cards wins the round.',
  ],
  rules: [
    'Deck has 4 colors (Red, Yellow, Green, Blue) with numbers 0-9 and action cards.',
    'Action cards: Skip (next player loses turn), Reverse (change direction), Draw Two (+2 cards to next player).',
    'Wild cards: Can be played on anything, player chooses next color.',
    'Wild Draw Four: Choose color + next player draws 4 (can only play if no matching color).',
    'Forgetting to say "UNO" with 1 card = draw 2 penalty if caught.',
    'Stacking Draw cards is a common house rule (not official).',
    'First to 500 points across rounds wins (card values: number cards = face value, action = 20, wild = 50).',
  ],
  tips: [
    'Save Wild and Draw Four cards for when you really need them.',
    'Change color to one you have many of.',
    'Keep track of what colors opponents are low on.',
    'Play high-value cards early to reduce penalty if someone else wins.',
    'Use Reverse and Skip strategically to control the flow.',
  ],
};

export const goInstructions: GameInstructions = {
  gameName: 'Go',
  steps: [
    'Black places first. Click an intersection to place your stone.',
    'Stones are placed on intersections, not inside squares.',
    'Surround opponent stones to capture them (remove from board).',
    'Surround empty intersections to claim territory.',
    'Pass your turn when you have no beneficial moves left.',
    'Game ends when both players pass consecutively.',
    'Player with more territory + captures wins (komi added to White\'s score).',
  ],
  rules: [
    'Played on a 19×19 grid (also 9×9 or 13×13 for beginners).',
    'Stones are placed on intersections and never move once placed.',
    'A stone or group with no empty adjacent intersections (liberties) is captured.',
    'Ko rule: Cannot immediately recapture a single stone that just captured yours.',
    'Suicide rule: Cannot place a stone that would have zero liberties (unless it captures).',
    'Territory = empty intersections completely surrounded by your stones.',
    'Komi: White receives 6.5 bonus points to compensate for going second.',
    'Score = Territory + Captures + Komi (for White).',
  ],
  tips: [
    'Focus on corners first, then edges, then center.',
    'Keep your groups connected — isolated stones are vulnerable.',
    'Don\'t try to save every stone. Sacrifice small groups for bigger gains.',
    'Learn basic life-and-death patterns (two eyes = alive).',
    'Influence vs territory: balance between solid territory and potential.',
  ],
};

export const hangmanInstructions: GameInstructions = {
  gameName: 'Hangman',
  steps: [
    'A secret word is chosen (shown as blank dashes).',
    'Guess one letter at a time by clicking or typing it.',
    'Correct guesses reveal all instances of that letter in the word.',
    'Wrong guesses add a body part to the hangman drawing.',
    'You have 6 wrong guesses before the hangman is complete.',
    'Guess the full word before running out of attempts to win.',
  ],
  rules: [
    'The word is hidden — only the number of letters (dashes) is shown.',
    'Each guess must be a single letter.',
    'Previously guessed letters cannot be guessed again.',
    'Correct letter: All occurrences are revealed.',
    'Wrong letter: One body part is drawn (head, body, arms, legs).',
    '6 wrong guesses = game over (hangman complete).',
    'You can guess the full word at any time (wrong = game over).',
  ],
  tips: [
    'Start with common vowels: E, A, I, O, U.',
    'Then try common consonants: T, N, S, R, L.',
    'Look at word length and revealed pattern for clues.',
    'Common endings: -ING, -TION, -ED, -LY.',
    'Don\'t guess rare letters (Q, X, Z) unless you\'re sure.',
  ],
};

export const minesweeperInstructions: GameInstructions = {
  gameName: 'Minesweeper',
  steps: [
    'Click any cell to start — the first click is always safe.',
    'Numbers show how many mines are in the 8 adjacent cells.',
    'Use numbers to deduce which cells are safe and which have mines.',
    'Right-click (or long-press) to flag a cell you think has a mine.',
    'Clear all non-mine cells to win.',
    'Clicking a mine = game over!',
  ],
  rules: [
    'Grid sizes: Beginner (9×9, 10 mines), Intermediate (16×16, 40 mines), Expert (30×16, 99 mines).',
    'Numbers 1-8 indicate adjacent mine count.',
    'Empty cells (0 adjacent mines) auto-reveal neighbors.',
    'Flags mark suspected mines — they prevent accidental clicks.',
    'The mine count display shows remaining unflagged mines.',
    'Timer starts on first click.',
    'Win by revealing all safe cells (flagging is optional).',
  ],
  tips: [
    'Start in corners or edges — they have fewer neighbors to analyze.',
    'If a "1" cell has only one unrevealed neighbor, that neighbor is a mine.',
    'Use the "1-2-1" pattern: the middle cell\'s mine is between the 1s.',
    'Flag obvious mines immediately to help with adjacent number analysis.',
    'When stuck, look for cells that are safe by elimination.',
  ],
};

export const sudokuInstructions: GameInstructions = {
  gameName: 'Sudoku',
  steps: [
    'The 9×9 grid is partially filled with numbers 1-9.',
    'Click an empty cell to select it.',
    'Enter a number (1-9) that doesn\'t conflict with the row, column, or 3×3 box.',
    'Use pencil marks (notes) to track possible numbers for a cell.',
    'Fill all 81 cells correctly to complete the puzzle.',
    'No guessing needed — every puzzle has a unique logical solution.',
  ],
  rules: [
    'Grid is 9×9, divided into nine 3×3 boxes.',
    'Each row must contain numbers 1-9 exactly once.',
    'Each column must contain numbers 1-9 exactly once.',
    'Each 3×3 box must contain numbers 1-9 exactly once.',
    'Pre-filled numbers (givens) cannot be changed.',
    'Every valid Sudoku has exactly one solution.',
  ],
  tips: [
    'Start with rows/columns/boxes that have the most filled cells.',
    'Use "naked singles" — cells where only one number is possible.',
    'Use "hidden singles" — a number that can only go in one cell within a row/column/box.',
    'Pencil marks help track possibilities for harder puzzles.',
    'Look for "pointing pairs" and "box/line reduction" for intermediate techniques.',
  ],
};

export const solitaireInstructions: GameInstructions = {
  gameName: 'Solitaire (Klondike)',
  steps: [
    '7 tableau columns are dealt: 1 card in first, 2 in second, etc. Top card face-up.',
    'Move cards between tableau columns in descending order, alternating colors.',
    'Reveal face-down cards by moving the card on top of them.',
    'Draw cards from the stock pile (top-left) when stuck.',
    'Build foundation piles (top-right) from Ace to King, by suit.',
    'Move all 52 cards to the 4 foundation piles to win.',
  ],
  rules: [
    'Tableau: Build down in alternating colors (red 7 on black 8).',
    'Only Kings can be placed in empty tableau columns.',
    'Foundation: Build up by suit from Ace to King.',
    'Stock: Draw 1 or 3 cards (depending on variant) to the waste pile.',
    'Top card of waste pile is playable to tableau or foundation.',
    'You can move partial or full sequences between tableau columns.',
    'Game is won when all 4 foundations are complete (A through K).',
  ],
  tips: [
    'Always play Aces and Twos to foundations immediately.',
    'Prioritize revealing face-down cards.',
    'Don\'t empty a tableau column unless you have a King to fill it.',
    'Keep foundation piles roughly even — don\'t build one far ahead.',
    'Think ahead before moving — some moves block future plays.',
  ],
};

export const game2048Instructions: GameInstructions = {
  gameName: '2048',
  steps: [
    'Use arrow keys (or swipe on mobile) to slide all tiles in one direction.',
    'Tiles with the same number merge when they collide.',
    'After each move, a new tile (2 or 4) appears in a random empty cell.',
    'Merged tiles double in value (2+2=4, 4+4=8, etc.).',
    'Reach the 2048 tile to win! (You can continue for higher scores.)',
    'Game over when no moves are possible (board full, no merges available).',
  ],
  rules: [
    'Played on a 4×4 grid.',
    'All tiles slide as far as possible in the chosen direction.',
    'Two identical tiles merge into one tile with double the value.',
    'A tile can only merge once per move.',
    'New tile (90% chance of 2, 10% chance of 4) spawns after each move.',
    'Score increases by the value of each merged tile.',
    'No moves possible = game over.',
  ],
  tips: [
    'Keep your highest tile in a corner (top-left or bottom-right).',
    'Never move your highest tile away from its corner.',
    'Build a "snake" pattern: highest in corner, descending along edges.',
    'Avoid moving up if your highest tile is in the bottom-left corner.',
    'Plan 2-3 moves ahead to avoid getting stuck.',
  ],
};

export const pinballInstructions: GameInstructions = {
  gameName: 'Pinball',
  steps: [
    'Press Space or click Launch to shoot the ball onto the playfield.',
    'Use Left/Right arrow keys (or A/D) to control the flippers.',
    'Hit bumpers, targets, and ramps to score points.',
    'Keep the ball in play — don\'t let it drain between the flippers.',
    'Activate multiball and bonus modes for high scores.',
    'Game ends when all balls are lost (typically 3 balls per game).',
  ],
  rules: [
    'You start with 3 balls.',
    'Flippers are controlled with left/right keys.',
    'Ball draining (falling below flippers) loses the current ball.',
    'Bumpers give points on contact.',
    'Targets and ramps give bonus points and activate features.',
    'Tilt: Excessive nudging ends the current ball.',
    'High score is saved locally.',
  ],
  tips: [
    'Don\'t mash both flippers — use one at a time for better control.',
    'Trap the ball (hold flipper up, let ball rest) to aim shots.',
    'Learn the table layout — know where ramps and targets are.',
    'Nudge gently to save a ball heading for the drain.',
    'Aim for lit targets to activate multipliers and bonuses.',
  ],
};

export const rummyInstructions: GameInstructions = {
  gameName: 'Rummy',
  steps: [
    'Each player is dealt cards (7 or 13 depending on variant).',
    'On your turn, draw a card from the stock pile or discard pile.',
    'Arrange your cards into valid sets and sequences (melds).',
    'A sequence is 3+ consecutive cards of the same suit (e.g., 4♥ 5♥ 6♥).',
    'A set is 3-4 cards of the same rank, different suits (e.g., 7♥ 7♦ 7♣).',
    'Discard one card to end your turn.',
    'Declare when all cards form valid melds (must have at least one pure sequence).',
  ],
  rules: [
    'Standard 52-card deck (+ jokers in some variants).',
    'Pure sequence: 3+ consecutive same-suit cards without joker.',
    'Impure sequence: Uses a joker as substitute.',
    'Set: 3-4 same-rank cards of different suits.',
    'At least one pure sequence is required to declare.',
    'Jokers (printed + wild) can substitute any card in impure melds.',
    'Invalid declaration = penalty (sum of ungrouped cards).',
    'First valid declaration wins. Points are calculated from opponents\' ungrouped cards.',
  ],
  tips: [
    'Prioritize forming a pure sequence first.',
    'Watch what opponents pick from the discard pile — it reveals their hand.',
    'Discard high-value cards early to minimize penalty if opponent declares.',
    'Keep flexible cards (middle cards like 5, 6, 7) that fit multiple sequences.',
    'Use jokers for high-value melds, not where a pure sequence is possible.',
  ],
};

export const dotsAndBoxesInstructions: GameInstructions = {
  gameName: 'Dots & Boxes',
  steps: [
    'The board is a grid of dots.',
    'On your turn, draw one line between two adjacent dots (horizontal or vertical).',
    'If your line completes a box (4th side), you claim it and go again.',
    'Completing a box earns you a point and an extra turn.',
    'Continue until all boxes are completed.',
    'Player with the most boxes wins.',
  ],
  rules: [
    'Standard grid is 5×5 dots (creating 4×4 = 16 possible boxes).',
    'Lines connect horizontally or vertically adjacent dots only.',
    'Each line can only be drawn once.',
    'Completing a box: Write your initial inside and take another turn.',
    'You can complete multiple boxes in one turn (chain reaction).',
    'Game ends when all possible lines are drawn.',
    'Most boxes wins. Ties are possible.',
  ],
  tips: [
    'Avoid drawing the third side of a box — it gives your opponent the completion.',
    'Force your opponent to open up chains (sequences of boxes).',
    'In the endgame, sacrifice small chains to gain larger ones.',
    'Count remaining boxes to plan your strategy.',
    'The "double-cross" strategy: Leave 2 boxes in a chain to force opponent to open the next chain.',
  ],
};

export const chauparInstructions: GameInstructions = {
  gameName: 'Chaupar',
  steps: [
    'Pawns start at fixed positions on the track (2 in outgoing lane, 2 in returning lane of your arm).',
    'Roll 3 long dice (values 1, 2, 5, 6) on your turn.',
    'Assign each die value to a different pawn to move it forward.',
    'Move pawns clockwise around the 68-square cross-shaped track.',
    'Land on an opponent\'s pawn to permanently eliminate it from the game.',
    'Form blocks with your teammate\'s pawns for protection.',
    'Navigate all your team\'s pawns back to the Charkoni (center) to win.',
  ],
  rules: [
    'Team game: Red+Green (Team A) vs Blue+Yellow (Team B).',
    'Each player has 4 pawns, starting pre-placed on the track.',
    'Traditional dice: 3 four-sided long dice with faces 1, 2, 5, 6.',
    'Dice values can be split among different pawns.',
    'Captured pawns are permanently eliminated (not returned to start).',
    'Two same-team pawns on one square form an impassable block.',
    'No safe/castle squares — any unblocked pawn can be captured.',
    'No grace throws or extra turns for special rolls.',
    'First team to get all 8 pawns (both partners) home wins.',
  ],
  tips: [
    'Coordinate with your partner to form blocks at key positions.',
    'Target isolated opponent pawns — captures are permanent and devastating.',
    'Assign dice values strategically: use small values for pawns near home.',
    'Don\'t spread your pawns too thin — keep them close for mutual protection.',
    'Prioritize eliminating opponents over racing home.',
  ],
};

export const golfCardGameInstructions: GameInstructions = {
  gameName: 'Golf Card Game',
  steps: [
    'Each player is dealt 6 cards face-down in a 2×3 grid.',
    'At the start of each round, flip 2 of your cards face-up (your choice).',
    'On your turn, draw a card from the draw pile or discard pile.',
    'Choose: replace any grid card (face-up or face-down) with the drawn card, OR discard it and flip one face-down card.',
    'When all your cards are face-up, you automatically knock (ending the round for others).',
    'After a knock, each other player gets one final turn.',
    'All cards are revealed and scored. Matching columns cancel out (score 0)!',
    'Play 9 rounds. Lowest cumulative score wins.',
  ],
  rules: [
    'Card values: A=1, 2-10=face value, J=10, Q=10, K=0.',
    'Each player has a 2×3 grid (6 cards total).',
    'At round start, each player reveals exactly 2 cards of their choice.',
    'Draw phase: Take from draw pile (unknown) or discard pile (visible top card).',
    'Replace: Swap drawn card with any grid card; replaced card goes to discard pile.',
    'Discard: Put drawn card on discard pile, then flip one of your face-down cards.',
    'Column-pair cancellation: If both cards in a column have the same rank, that column scores 0.',
    'Knock penalty: If you knock but don\'t have the lowest score, add 10 points.',
    'Game is 9 rounds. Lowest total score across all rounds wins.',
    'Ties are possible if multiple players share the lowest score.',
  ],
  tips: [
    'Kings are worth 0 — keep them and try to pair them in a column for free cancellation.',
    'Try to create column pairs (same rank in both rows of a column) to cancel points.',
    'Drawing from the discard pile is safe — you know what you\'re getting.',
    'Knock early if you have a strong hand to pressure opponents.',
    'Be careful knocking — the 10-point penalty hurts if someone beats your score.',
    'Track which cards opponents have revealed to estimate their scores.',
  ],
};

export const wordleInstructions: GameInstructions = {
  gameName: 'Daily Word',
  steps: [
    'You have 6 attempts to guess a secret 5-letter word.',
    'Type a valid 5-letter word and press Enter to submit your guess.',
    'After each guess, the tiles change color to show how close your guess was.',
    'Green means the letter is correct and in the right position.',
    'Yellow means the letter is in the word but in the wrong position.',
    'Gray means the letter is not in the word at all.',
    'Use the clues to refine your next guess. Keep guessing until you find the word!',
  ],
  rules: [
    'The secret word is always 5 letters long.',
    'You have a maximum of 6 guesses.',
    'Each guess must be a valid English word.',
    'Duplicate letters are handled: if the word has one L and you guess two, only one is colored.',
    'In Daily mode, everyone gets the same word each day and you can only play once.',
    'In Hard mode, you must use all revealed hints (green/yellow letters) in subsequent guesses.',
    'Practice mode gives a random word each time with no stats impact.',
  ],
  tips: [
    'Start with a word that has common vowels and consonants (e.g., CRANE, SLATE, AUDIO).',
    'Avoid repeating letters that have been marked gray.',
    'Use the on-screen keyboard colors to track which letters you have tried.',
    'In Hard mode, plan carefully since you must reuse all confirmed information.',
    'Try to place yellow letters in different positions on your next guess.',
    'Common letter patterns: -IGHT, -OUND, -TION can help narrow options.',
  ],
};

/** Map of game IDs to their instructions for easy lookup */
export const allGameInstructions: Record<string, GameInstructions> = {
  'tic-tac-toe': tictactoeInstructions,
  'chess': chessInstructions,
  'checkers': checkersInstructions,
  'connect-four': connectFourInstructions,
  'battleship': battleshipInstructions,
  'poker': pokerInstructions,
  'mafia': mafiaInstructions,
  'ludo': ludoInstructions,
  'snakes-and-ladders': snakesAndLaddersInstructions,
  'teen-patti': teenPattiInstructions,
  'reversi': reversiInstructions,
  'mancala': mancalaInstructions,
  'pachisi': pachisiInstructions,
  'carrom': carromInstructions,
  'backgammon': backgammonInstructions,
  'uno': unoInstructions,
  'go': goInstructions,
  'hangman': hangmanInstructions,
  'minesweeper': minesweeperInstructions,
  'sudoku': sudokuInstructions,
  'solitaire': solitaireInstructions,
  '2048': game2048Instructions,
  'pinball': pinballInstructions,
  'rummy': rummyInstructions,
  'dots-and-boxes': dotsAndBoxesInstructions,
  'chaupar': chauparInstructions,
  'golf-card-game': golfCardGameInstructions,
  'wordle': wordleInstructions,
};
